import { Box, Flex, Text } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import React, { memo, useCallback, useEffect, useRef } from "react";

import { useColorModeValue } from "@components/ui/color-mode";
import NavigationEntityIcon from "@features/services/NavigationEntityIcon";
import { NavigationEntity, useIsSelected } from "hooks/useNavigationEntities";

interface Props {
  entity: NavigationEntity;
  depth?: number;
  onSelect?: (entity: NavigationEntity) => void;
}

export const NavigationItem: React.FC<Props> = React.memo(
  ({ entity, depth = 0, onSelect }) => {
    const isSelected = useIsSelected(entity);
    const isDark = useColorModeValue(false, true);
    const router = useRouter();

    const onClick = useCallback(() => {
      if (entity.absolutePath != null && !isSelected) {
        router.push(entity.absolutePath);
        onSelect?.(entity);
      }
    }, [entity, router, isSelected, onSelect]);

    useEffect(() => {
      const path = entity.absolutePath;
      if (path != null) {
        router.prefetch(path);
      }
    }, [entity, router]);

    const itemRef = useRef<HTMLDivElement>(null);

    let background: string | undefined;
    if (isSelected) {
      background = "sidebar.hover";
    }

    const indentationPaddingLeft = `${depth * 8}px`;
    return (
      <>
        <Box data-testid="nav-item" position="relative">
          <Flex
            ref={itemRef}
            data-testid={`nav-${entity.name}`}
            tabIndex={-1}
            role="navigation"
            alignItems="center"
            marginX={1}
            borderRadius="md"
            cursor="pointer"
            height={7}
            transition="background .2s"
            bgColor={background}
            color={
              isDark
                ? isSelected
                  ? "surface.background"
                  : "text.primary"
                : undefined
            }
            _hover={
              isDark
                ? { bgColor: "sidebar.hover", color: "white" }
                : { bgColor: "sidebar.hover" }
            }
            onClick={onClick}
          >
            {depth > 0 && (
              <div style={{ paddingLeft: indentationPaddingLeft }} />
            )}
            <Flex flex="1" gap="2px" overflow="hidden">
              <EmptySpace depth={depth} />
              <Flex
                alignItems="center"
                justifyContent="center"
                minHeight={7}
                minWidth={7}
              >
                <NavigationEntityIcon entity={entity} />
              </Flex>
              <Flex
                flex="1"
                fontSize="sm"
                fontWeight={depth === 0 ? "medium" : undefined}
                alignItems="center"
                color="text.primary"
              >
                <Text wordBreak="break-all">{entity.name}</Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </>
    );
  },
);

NavigationItem.displayName = "NavigationItem";

const EmptySpace = React.memo(({ depth }: { depth: number }) => {
  return <div style={{ paddingLeft: depth === 0 ? 0 : "4px" }} />;
});

EmptySpace.displayName = "EmptySpace";
